import { agentOf, elapsed, shortenPath, tailPath } from "@foreman/app-core";
import { sessionActivity, type InboxEvent, type SessionInfo } from "@foreman/protocol";
import { rememberedTitle } from "./session-titles";
import { AgentLogo } from "./ui";

/** A session that produced output this recently is "Working". */
export const LIVE_WITHIN_MS = 2 * 60_000;

/** A small outlined folder, drawn before a directory path. */
export const FolderIcon = ({ size = 13 }: { size?: number }) => (
  <svg className="folder-ico" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

export function sessionTitle(s: SessionInfo): string {
  const agent = agentOf(s.command);
  return s.title ?? rememberedTitle(s.id) ?? (agent.key !== "other" ? "New conversation" : s.command);
}

/** One session in a list: a status glyph, the title, where it runs, which agent, how long. */
export function SessionRow(props: { session: SessionInfo; ask?: InboxEvent; computerLabel?: string; now: number; onOpen: () => void; /** Leave out the folder: the list is already grouped under it. */ hideDir?: boolean; /** Shows a Remove button: forget this session (only offered for one that has ended). */ onRemove?: () => void }) {
  const { session: s, ask } = props;
  const agent = agentOf(s.command);
  const ended = s.status !== "running";
  const glyph = ended ? "end" : ask ? "wait" : "run";
  const quiet = !ask && sessionActivity(s, props.now) !== "active"; // untouched for over an hour: say so instead of how long ago it started
  const live = !ask && !ended && s.lastActivityAt !== undefined && props.now - s.lastActivityAt < LIVE_WITHIN_MS; // output in the last two minutes: the agent is working right now
  const row = (
    <button className={"session-row" + (ask ? " need" : "") + (ended ? " ended" : "")} onClick={props.onOpen}>
      <span className={`st ${glyph}`} role="img" aria-label={ended ? "Ended" : ask ? "Needs you" : "Running"} />
      <span className="row-text">
        <span className="session-title">{sessionTitle(s)}</span>
        {ask ? (
          <span className="session-ask">{ask.title}</span>
        ) : (
          <span className="session-meta">
            {live && <span className="session-live">Working</span>}
            <span className="agent-name">{agent.label}</span>
            {props.computerLabel && <span className="session-where">{props.computerLabel}</span>}
            {!props.hideDir && (
              <span className="session-dir" title={shortenPath(s.cwd)}>
                <FolderIcon size={11} />
                {tailPath(shortenPath(s.cwd), 30)}
              </span>
            )}
          </span>
        )}
      </span>
      <span className={`agent-badge ${agent.key}`} aria-hidden>
        <AgentLogo agent={agent.key} size={16} />
      </span>
      <span className="session-age">
        {quiet ? `idle ${elapsed(s.lastActivityAt!, props.now)}` : elapsed(s.startedAt, props.now)}
      </span>
    </button>
  );
  if (!props.onRemove) return row;
  return (
    <div className="session-row-wrap">
      {row}
      <button className="row-remove" aria-label={`Remove ${sessionTitle(s)} from the list`} onClick={props.onRemove}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="m6 6 12 12M18 6 6 18" />
        </svg>
      </button>
    </div>
  );
}
